import { apiFetch } from './client.js'

function extractList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function extractItem(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.item) return payload.item
    if (payload.data && !Array.isArray(payload.data)) return payload.data
  }
  return payload
}

function normalizeAbVariant(variant) {
  if (!variant || typeof variant !== 'object') return null
  const elements = Array.isArray(variant.elements)
    ? variant.elements
      .map((binding) => {
        if (!binding || typeof binding !== 'object') return null
        return {
          id: binding.id,
          applicationId: binding.application_id || binding.applicationId,
          abVariantId: binding.ab_variant_id || binding.abVariantId,
          elementId: Number(binding.element_id ?? binding.elementId),
          conditionExpression: binding.condition_expression || binding.conditionExpression || '',
          order: Number(binding.order || 0),
          createdAt: binding.created_at || binding.createdAt || null,
          updatedAt: binding.updated_at || binding.updatedAt || null,
        }
      })
      .filter(Boolean)
    : []
  return {
    id: variant.id,
    applicationId: variant.application_id || variant.applicationId,
    abTestId: variant.ab_test_id || variant.abTestId,
    name: variant.name || '',
    order: Number(variant.order || 0),
    trafficPercent: Number(variant.traffic_percent ?? variant.trafficPercent ?? 0),
    isDefault: Boolean(variant.is_default ?? variant.isDefault),
    createdAt: variant.created_at || variant.createdAt || null,
    updatedAt: variant.updated_at || variant.updatedAt || null,
    elements,
  }
}

function normalizeAbTest(abTest) {
  if (!abTest || typeof abTest !== 'object') return null
  const variants = extractList(abTest.variants).map(normalizeAbVariant).filter(Boolean)
  const segmentationId = abTest.segmentation_id ?? abTest.segmentationId ?? null
  const groupSegmentId = abTest.group_segment_id ?? abTest.groupSegmentId ?? null
  return {
    id: abTest.id,
    applicationId: abTest.application_id || abTest.applicationId,
    groupConfigId: abTest.group_config_id || abTest.groupConfigId,
    groupConfigName: abTest.group_config_name || abTest.groupConfigName || '',
    groupSegmentId: groupSegmentId ? String(groupSegmentId) : null,
    segmentationId: segmentationId ? String(segmentationId) : null,
    segmentKey: segmentationId ? String(segmentationId) : (abTest.segment_key || abTest.segmentKey || ''),
    segmentName: abTest.segment_name || abTest.segmentName || '',
    name: abTest.name || '',
    description: abTest.description || '',
    status: abTest.status || 'Draft',
    availableStatuses: Array.isArray(abTest.available_statuses)
      ? abTest.available_statuses
      : Array.isArray(abTest.availableStatuses)
        ? abTest.availableStatuses
        : null,
    isEnabled: Boolean(abTest.is_enabled ?? abTest.isEnabled),
    isOnlyNewUser: Boolean(abTest.is_only_new_user ?? abTest.isOnlyNewUser),
    automaticDistributionStop: Boolean(
      abTest.automatic_distribution_stop ?? abTest.automaticDistributionStop,
    ),
    releaseAllVersions: Boolean(abTest.release_all_versions ?? abTest.releaseAllVersions),
    audience: abTest.audience ?? null,
    targetMetric: abTest.target_metric ?? abTest.targetMetric ?? '',
    metricDays: abTest.metric_days ?? abTest.metricDays ?? null,
    targetUserCount: abTest.target_user_count ?? abTest.targetUserCount ?? null,
    startedAt: abTest.started_at || abTest.startedAt || null,
    finishedAt: abTest.finished_at || abTest.finishedAt || null,
    createdAt: abTest.created_at || abTest.createdAt || null,
    updatedAt: abTest.updated_at || abTest.updatedAt || null,
    variants,
  }
}

export async function listAbTests(applicationId, params = {}) {
  const query = new URLSearchParams()
  if (params.groupConfigId !== undefined && params.groupConfigId !== null) {
    query.set('group_config_id', String(params.groupConfigId))
  }
  if (params.groupSegmentId) {
    query.set('group_segment_id', String(params.groupSegmentId))
  }
  if (params.remoteConfigId) {
    query.set('remote_config_id', String(params.remoteConfigId))
  }
  const suffix = query.toString() ? `?${query.toString()}` : ''
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/ab-tests${suffix}`)
  return extractList(payload).map(normalizeAbTest).filter(Boolean)
}

export async function createAbTest(applicationId, values) {
  const variants = Array.isArray(values?.variants)
    ? values.variants.map((v) => ({
        name: String(v?.name || '').trim(),
        traffic_percent: Number(v?.trafficPercent ?? v?.traffic_percent ?? 0),
        is_default: Boolean(v?.isDefault ?? v?.is_default),
        elements: (Array.isArray(v?.elements) ? v.elements : [])
          .map((e) => ({
            element_id: Number(e?.elementId ?? e?.element_id),
            condition_expression:
              e?.conditionExpression ?? e?.condition_expression ?? e?.condition ?? null,
          }))
          .filter((e) => Number.isInteger(e.element_id) && e.element_id > 0),
      }))
    : []
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/ab-tests`, {
    method: 'POST',
    body: {
      group_config_id: Number(values?.groupConfigId),
      group_segment_id: values?.groupSegmentId ? String(values.groupSegmentId).trim() : null,
      name: String(values?.name || '').trim(),
      description: String(values?.description || '').trim(),
      status: String(values?.status || 'Draft').trim(),
      audience: values?.audience ?? null,
      target_metric: String(values?.targetMetric || '').trim() || null,
      metric_days: values?.metricDays ?? null,
      target_user_count: values?.targetUserCount ?? null,
      is_only_new_user: Boolean(values?.isOnlyNewUser),
      automatic_distribution_stop: Boolean(values?.automaticDistributionStop),
      release_all_versions: Boolean(values?.releaseAllVersions),
      test_finish_date: values?.testFinishDate || null,
      variants: variants.length > 0 ? variants : undefined,
    },
  })
  return normalizeAbTest(extractItem(payload))
}

export async function setAbTestStatus(applicationId, abTestId, status) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/ab-tests/${encodeURIComponent(abTestId)}/status`,
    {
      method: 'POST',
      body: {
        status: String(status || '').trim(),
      },
    },
  )
  return normalizeAbTest(extractItem(payload))
}
